/**
 * Pricing Modal
 * 
 * Free / Pro / Business 요금제 안내 모달
 */

import { useState, useEffect } from 'react';
import { useLanguage, parseUTCDate } from '../i18n';
import { useAuth } from '../auth';
import { analytics } from '../analytics';
import { useBodyScrollLock } from '../hooks';
import { 
  devSubscribe, 
  devCancelSubscription, 
  devReactivateSubscription,
  getSubscription,
  APP_ENV 
} from '../api';
import EmailAuthModal from './EmailAuthModal';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// 체크 아이콘
const CheckIcon = () => (
  <svg className="w-4 h-4 text-status-go flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

// 대시 아이콘 (비활성)
const DashIcon = () => (
  <svg className="w-4 h-4 text-terminal-dim flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

interface PlanCardProps {
  name: string;
  description: string;
  price: string;
  originalPrice?: string;
  priceNote?: string;
  annualTotal?: string;
  period: string;
  features: { text: string; enabled: boolean }[];
  isPopular?: boolean;
  isCurrent?: boolean;
  buttonText: string;
  buttonVariant: 'primary' | 'secondary';
  onSelect: () => void;
  isProcessing?: boolean;
  isAnnual?: boolean;
}

function PlanCard({
  name,
  description,
  price,
  originalPrice,
  priceNote,
  annualTotal,
  period,
  features,
  isPopular,
  isCurrent,
  buttonText,
  buttonVariant,
  onSelect,
  isProcessing,
  isAnnual,
}: PlanCardProps) {
  return (
    <div
      className={`relative rounded-lg p-4 transition-all flex flex-col h-full ${
        isCurrent
          ? 'border-2 border-status-go bg-status-go/10'
          : isPopular
          ? 'border-2 border-status-go bg-status-go/5'
          : 'border border-terminal-border bg-terminal-dim/10 hover:border-terminal-muted'
      }`}
    >
      {isCurrent && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-status-go text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
          현재
        </div>
      )}
      {!isCurrent && isPopular && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-status-go text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
          추천
        </div>
      )}
      
      <div className="mb-3">
        <h3 className="text-base font-bold text-terminal-text">{name}</h3>
        <p className="text-xs text-terminal-muted">{description}</p>
      </div>
      
      <div className="mb-3 h-[6.5rem]">
        {/* 원가 (취소선) - 연간일 때만 */}
        <div className="h-4">
          {originalPrice && (
            <p className="text-terminal-dim text-xs line-through">{originalPrice}</p>
          )}
        </div>
        {/* 현재 가격 */}
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-status-go">{price}</span>
          <span className="text-terminal-muted text-xs">{period}</span>
        </div>
        {/* 연간 총액 (볼드) - 연간일 때만 */}
        <div className="h-4">
          {isAnnual && annualTotal && (
            <p className="text-xs font-semibold text-status-go">{annualTotal}</p>
          )}
        </div>
        {/* 설명 문구 */}
        <div className="h-4">
          {priceNote && (
            <p className="text-[10px] text-terminal-muted mt-0.5">{priceNote}</p>
          )}
        </div>
      </div>
      
      {/* flex-1로 남은 공간 채우기 → 버튼이 항상 하단에 정렬됨 */}
      <ul className="space-y-1.5 flex-1">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-1.5">
            {feature.enabled ? <CheckIcon /> : <DashIcon />}
            <span className={`text-xs ${feature.enabled ? 'text-terminal-text' : 'text-terminal-dim'}`}>
              {feature.text}
            </span>
          </li>
        ))}
      </ul>
      
      <button
        onClick={onSelect}
        disabled={isCurrent || isProcessing}
        className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-all mt-4 ${
          isCurrent
            ? 'bg-terminal-dim/50 border border-terminal-border text-terminal-muted cursor-default'
            : isProcessing
            ? 'bg-terminal-dim/50 border border-terminal-border text-terminal-muted cursor-wait'
            : buttonVariant === 'primary'
            ? 'bg-status-go text-white hover:bg-status-go/90'
            : 'border border-terminal-border text-terminal-text hover:bg-terminal-dim/30'
        }`}
      >
        {isProcessing ? '처리 중...' : buttonText}
      </button>
    </div>
  );
}

export default function PricingModal({ isOpen, onClose }: PricingModalProps) {
  const { t, lang } = useLanguage();
  const { isAuthenticated, startGithubLogin, quota } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionEndsAt, setSubscriptionEndsAt] = useState<string | null>(null);
  const [subscriptionStartedAt, setSubscriptionStartedAt] = useState<string | null>(null);
  const [nextBillingAt, setNextBillingAt] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isBeta, setIsBeta] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annually'>('monthly');
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);
  const [showTeamContactPopup, setShowTeamContactPopup] = useState(false);
  const [showBetaDowngradePopup, setShowBetaDowngradePopup] = useState(false);
  
  // 새로운 인증 모달 상태
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showEmailAuthModal, setShowEmailAuthModal] = useState(false);
  
  const isKo = lang === 'ko';
  // 비로그인 상태에서는 currentPlan을 null로 설정 (Free도 로그인 필요)
  const currentPlan = isAuthenticated ? (quota?.plan || 'free') : null;
  const isAnnual = billingPeriod === 'annually';
  const discountRate = 0.2; // 20% 할인
  
  // 구독 상태 조회
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      getSubscription().then((info) => {
        setSubscriptionStartedAt(info.subscription_started_at);
        setSubscriptionEndsAt(info.subscription_ends_at);
        setNextBillingAt(info.next_billing_at);
        setIsCancelling(info.is_cancelling);
        setIsBeta(info.is_beta);
      }).catch(() => {
        // 조회 실패 시 무시
      });
    }
  }, [isOpen, isAuthenticated]);
  
  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  // 플랜 선택 핸들러
  const handleSelectPlan = async (planName: string) => {
    setError(null);
    
    // 현재 플랜과 같으면 무시
    const targetPlan = planName.toLowerCase() as 'free' | 'pro' | 'max' | 'team';
    if (currentPlan === targetPlan) {
      onClose();
      return;
    }
    
    // GA4: 플랜 선택 이벤트
    analytics.planSelect(targetPlan, billingPeriod);
    
    // 로그인 확인 - 새로운 인증 모달 표시
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;  // 로그인 완료 후 다시 플랜 선택 처리
    }

    // 베타 기간(~2026.02.28): 모든 사용자는 Max 무료 제공
    // - Free/Pro 다운그레이드 불가 → 안내 팝업
    // - Team 플랜은 별도 문의 → 문의 팝업
    if (isBeta) {
      if (targetPlan === 'team') {
        setShowTeamContactPopup(true);
        return;
      }
      if (targetPlan === 'free' || targetPlan === 'pro') {
        setShowBetaDowngradePopup(true);
        return;
      }
    }
    
    setIsProcessing(true);
    
    console.log(`[PricingModal] 플랜 변경 시작: ${currentPlan} → ${targetPlan}`);
    console.log(`[PricingModal] 결제 주기: ${billingPeriod}`);
    
    try {
      // 🧪 로컬 환경: 결제 없이 바로 플랜 변경 (테스트용)
      if (APP_ENV === 'local') {
        console.log(`[PricingModal] 로컬 환경 - devSubscribe 호출: ${targetPlan}`);
        await devSubscribe(targetPlan);
        
        console.log(`[PricingModal] 플랜 변경 완료, 페이지 새로고침`);
        // quota 새로고침을 위해 페이지 새로고침
        window.location.reload();
        return;
      }
      
      // staging/production: 결제는 2월부터 가능
      if (targetPlan === 'free') {
        // Free 플랜도 선물 팝업 표시
        setShowComingSoonPopup(true);
        setIsProcessing(false);
        return;
      }
      
      if (targetPlan === 'team') {
        // Team 플랜은 문의 팝업 표시
        setShowTeamContactPopup(true);
        setIsProcessing(false);
        return;
      }
      
      // 결제 기능은 아직 준비 중 - 팝업 표시
      setShowComingSoonPopup(true);
      setIsProcessing(false);
      return;
    } catch (err) {
      setError(err instanceof Error ? err.message : (isKo ? '결제 처리 중 오류가 발생했습니다.' : 'Payment processing error.'));
    } finally {
      setIsProcessing(false);
    }
  };

  // 구독 해지 핸들러 (로컬 환경 전용)
  const handleCancelSubscription = async () => {
    if (APP_ENV !== 'local') return;
    
    setError(null);
    setIsProcessing(true);
    
    try {
      const result = await devCancelSubscription();
      setSubscriptionEndsAt(result.subscription_ends_at);
      setIsCancelling(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : (isKo ? '해지 처리 중 오류가 발생했습니다.' : 'Cancellation error.'));
    } finally {
      setIsProcessing(false);
    }
  };
  
  // 구독 재활성화 핸들러 (로컬 환경 전용)
  const handleReactivateSubscription = async () => {
    if (APP_ENV !== 'local') return;
    
    setError(null);
    setIsProcessing(true);
    
    try {
      await devReactivateSubscription();
      setSubscriptionEndsAt(null);
      setIsCancelling(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : (isKo ? '재활성화 처리 중 오류가 발생했습니다.' : 'Reactivation error.'));
    } finally {
      setIsProcessing(false);
    }
  };

  // 인증 모달 닫기 핸들러
  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
  };
  
  // 플랜 순서 (낮은 순서가 낮은 등급)
  const planOrder: Record<string, number> = { free: 0, pro: 1, max: 2, team: 3 };
  
  // 버튼 텍스트 결정
  const getButtonText = (planName: string) => {
    const planKey = planName.toLowerCase() as 'free' | 'pro' | 'max' | 'team';
    
    // 비로그인 상태: 모든 플랜에 "시작하기" 표시
    if (currentPlan === null) {
      if (planKey === 'free') {
        return isKo ? '무료로 시작하기' : 'Start Free';
      }
      return isKo ? `${planName} 시작하기` : `Start with ${planName}`;
    }
    
    if (currentPlan === planKey) {
      return isKo ? '현재 플랜' : 'Current Plan';
    }
    
    const currentOrder = planOrder[currentPlan] ?? 0;
    const targetOrder = planOrder[planKey] ?? 0;
    
    if (targetOrder > currentOrder) {
      // 업그레이드
      return isKo ? `${planName} 업그레이드` : `Upgrade to ${planName}`;
    } else {
      // 다운그레이드
      return isKo ? `${planName} 다운그레이드` : `Downgrade to ${planName}`;
    }
  };
  
  // 해지 예정일 포맷팅
  const formatEndDate = (dateStr: string) => {
    const date = parseUTCDate(dateStr);
    if (isKo) {
      return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
    }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const addDays = (dateStr: string, days: number) => {
    const d = parseUTCDate(dateStr);
    return new Date(d.getTime() + days * 24 * 60 * 60 * 1000);
  };

  const formatDateObj = (date: Date) => {
    if (isKo) {
      return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
    }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // 가격 계산 헬퍼
  const formatPrice = (monthlyKrw: number, monthlyUsd: number) => {
    if (monthlyKrw === 0) {
      return { price: isKo ? '₩0' : '$0', period: isKo ? '/월' : '/mo' };
    }
    
    if (isAnnual) {
      const discountedKrw = Math.round(monthlyKrw * (1 - discountRate) / 100) * 100;
      const discountedUsd = Math.round(monthlyUsd * (1 - discountRate) * 100) / 100;
      const annualKrw = discountedKrw * 12;
      const annualUsd = discountedUsd * 12;
      return {
        price: isKo ? `₩${discountedKrw.toLocaleString()}` : `$${discountedUsd.toFixed(2)}`,
        period: isKo ? '/월' : '/mo',
        originalPrice: isKo ? `₩${monthlyKrw.toLocaleString()}` : `$${monthlyUsd.toFixed(2)}`,
        annualTotal: isKo ? `연 ₩${annualKrw.toLocaleString()}` : `$${annualUsd.toFixed(0)}/yr`,
      };
    }
    
    return {
      price: isKo ? `₩${monthlyKrw.toLocaleString()}` : `$${monthlyUsd.toFixed(2)}`,
      period: isKo ? '/월' : '/mo',
    };
  };

  const proPricing = formatPrice(3900, 3.99);
  const maxPricing = formatPrice(9900, 9.99);
  const teamPricing = formatPrice(18900, 18.99);

  const plans = [
    {
      name: 'Free',
      description: isKo ? '리포트 뷰어만' : 'Report viewer only',
      price: isKo ? '₩0' : '$0',
      period: isKo ? '/월' : '/mo',
      features: [
        { text: isKo ? '평가 실행' : 'Run evaluations', enabled: false },
        { text: isKo ? '리포트 열람' : 'View reports', enabled: true },
        { text: isKo ? '히스토리 3건' : '3 history', enabled: true },
        { text: isKo ? '리포트 뷰어' : 'Report Viewer', enabled: true },
      ],
      isPopular: false,
      isCurrent: currentPlan === 'free',
      buttonVariant: 'secondary' as const,
    },
    {
      name: 'Pro',
      description: isKo ? '개인 사용자용' : 'For individuals',
      price: proPricing.price,
      originalPrice: proPricing.originalPrice,
      priceNote: isKo ? '☕ 커피 한 잔' : '☕ A coffee',
      annualTotal: proPricing.annualTotal,
      period: proPricing.period,
      features: [
        { text: isKo ? '월 10회 평가' : '10 evals/mo', enabled: true },
        { text: isKo ? '히스토리 10건' : '10 history', enabled: true },
        { text: isKo ? '파일 3개 동시 첨부' : '3 files attach', enabled: true },
        { text: isKo ? '리포트 다운로드' : 'Report Download', enabled: true },
      ],
      isPopular: currentPlan === 'free' || currentPlan === null,  // Free 플랜이거나 비로그인 시 추천 표시
      isCurrent: currentPlan === 'pro',
      buttonVariant: 'primary' as const,
    },
    {
      name: 'Max',
      description: isKo ? '파워 유저용' : 'For power users',
      price: maxPricing.price,
      originalPrice: maxPricing.originalPrice,
      priceNote: isKo ? '🚀 무제한' : '🚀 Unlimited',
      annualTotal: maxPricing.annualTotal,
      period: maxPricing.period,
      features: [
        { text: isKo ? '무제한 평가' : 'Unlimited evals', enabled: true },
        { text: isKo ? '히스토리 100건' : '100 history', enabled: true },
        { text: isKo ? '개인 트렌드 분석' : 'Personal trends', enabled: true },
        { text: isKo ? '리포트 다운로드' : 'Report Download', enabled: true },
      ],
      isPopular: false,
      isCurrent: currentPlan === 'max',
      buttonVariant: 'secondary' as const,
    },
    {
      name: 'Team',
      description: isKo ? '팀 협업용' : 'For teams',
      price: teamPricing.price,
      originalPrice: teamPricing.originalPrice,
      priceNote: isKo ? '👥 최대 5명' : '👥 Up to 5',
      annualTotal: teamPricing.annualTotal,
      period: teamPricing.period,
      features: [
        { text: isKo ? '무제한 평가' : 'Unlimited evals', enabled: true },
        { text: isKo ? '팀 트렌드 + 대시보드' : 'Team trends + Dashboard', enabled: true },
        { text: isKo ? '팀 5명' : '5 members', enabled: true },
        { text: isKo ? 'API 접근' : 'API access', enabled: true },
      ],
      isPopular: false,
      isCurrent: currentPlan === 'team',
      buttonVariant: 'secondary' as const,
    },
  ];

  const faqs = [
    {
      q: isKo ? '베타 기간에는 무료인가요?' : 'Is it free during beta?',
      a: isKo
        ? '베타 기간(~2026.02.28) 동안 모든 사용자는 Max 플랜을 무료로 이용할 수 있습니다.'
        : 'During beta (until 2026.02.28), all users can use the Max plan for free.',
    },
    {
      q: isKo ? '연간 결제 할인이 있나요?' : 'Is there an annual discount?',
      a: isKo
        ? '연간 결제 시 20% 할인이 적용됩니다.'
        : 'Yes, 20% discount for annual billing.',
    },
    {
      q: isKo ? '플랜 변경은 언제든 가능한가요?' : 'Can I change plans anytime?',
      a: isKo
        ? '베타 기간에는 Free/Pro로 변경(다운그레이드)할 수 없습니다. Team 플랜은 support@releasegate.app 으로 문의해주세요.'
        : 'During beta, you can’t downgrade to Free/Pro. For the Team plan, contact support@releasegate.app.',
    },
  ];

  return (
    <>
      {/* 베타 기간 다운그레이드 불가 팝업 */}
      {showBetaDowngradePopup && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowBetaDowngradePopup(false)}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-sm p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-5xl mb-4">🔒</div>
            <h3 className="text-lg font-bold text-terminal-text mb-2">
              {isKo ? '베타 기간에는 변경할 수 없어요' : 'Plan changes are locked during beta'}
            </h3>
            <p className="text-terminal-muted mb-4">
              {isKo
                ? '베타 기간(~2026.02.28) 동안 모든 사용자는 Max 플랜을 무료로 이용합니다. Free/Pro 플랜으로 변경(다운그레이드)은 지원하지 않습니다.'
                : 'During beta (until 2026.02.28), all users get Max plan for free. Downgrading to Free/Pro is not supported.'}
            </p>
            <button
              onClick={() => setShowBetaDowngradePopup(false)}
              className="px-6 py-2 bg-status-go text-white font-medium rounded-lg hover:bg-status-go/90 transition-colors"
            >
              {isKo ? '확인' : 'OK'}
            </button>
          </div>
        </div>
      )}

      {/* Coming Soon 팝업 */}
      {showComingSoonPopup && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowComingSoonPopup(false)}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-sm p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-5xl mb-4">🎁</div>
            <h3 className="text-lg font-bold text-terminal-text mb-2">
              {isKo ? '지금은 무료로 드려요!' : 'It\'s Free Right Now!'}
            </h3>
            <p className="text-terminal-muted mb-4">
              {isKo 
                ? '2월 28일까지 개인 플랜 최상위인 Max의 모든 기능을 무료로 드려요! 무제한 평가, 트렌드 분석까지 마음껏 즐겨보세요. 결제는 3월부터 가능합니다. 🎉' 
                : 'Until Feb 28, enjoy all features of Max - our top-tier personal plan - for FREE! Unlimited evaluations, trend analysis, and more. Payment will be available from March. 🎉'}
            </p>
            <button
              onClick={() => setShowComingSoonPopup(false)}
              className="px-6 py-2 bg-status-go text-white font-medium rounded-lg hover:bg-status-go/90 transition-colors"
            >
              {isKo ? '확인' : 'OK'}
            </button>
          </div>
        </div>
      )}
      
      {/* Team 플랜 문의 팝업 */}
      {showTeamContactPopup && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowTeamContactPopup(false)}
        >
          <div
            className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-sm p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-5xl mb-4">👥</div>
            <h3 className="text-lg font-bold text-terminal-text mb-2">
              {isKo ? 'Team 플랜 문의' : 'Team Plan Inquiry'}
            </h3>
            <p className="text-terminal-muted mb-4">
              {isKo 
                ? 'Team 플랜에 관심을 가져주셔서 감사합니다! 아래 이메일로 문의해 주시면 친절하게 안내해 드리겠습니다. 📧' 
                : 'Thank you for your interest in the Team plan! Please contact us via email below and we\'ll be happy to assist you. 📧'}
            </p>
            <a
              href="mailto:support@releasegate.app"
              className="inline-block px-6 py-2 bg-status-go text-white font-medium rounded-lg hover:bg-status-go/90 transition-colors mb-3"
            >
              support@releasegate.app
            </a>
            <button
              onClick={() => setShowTeamContactPopup(false)}
              className="block w-full px-6 py-2 text-terminal-muted hover:text-terminal-text transition-colors"
            >
              {isKo ? '닫기' : 'Close'}
            </button>
          </div>
        </div>
      )}
      
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <div
          className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
        {/* 헤더 */}
        <div className="sticky top-0 bg-terminal-bg border-b border-terminal-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-terminal-text flex items-center gap-2">
              {t.pricing.title}
              <span className="text-xs font-medium text-status-go border border-status-go/50 px-2 py-0.5 rounded">
                BETA
              </span>
            </h2>
            <p className="text-sm text-terminal-muted mt-1">{t.pricing.subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-terminal-dim/30 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-terminal-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 요금제 카드 */}
        <div className="p-6">
          {/* 에러 메시지 */}
          {error && (
            <div className="mb-4 p-3 bg-status-danger/10 border border-status-danger/30 rounded text-status-danger text-sm">
              {error}
            </div>
          )}

          {/* (로컬 테스트 모드) */}
          {APP_ENV === 'local' && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
              <p className="text-sm text-purple-400">
                <span className="font-semibold">🧪 {isKo ? '로컬 테스트 모드' : 'Local Test Mode'}:</span>{' '}
                {isKo
                  ? '결제 없이 플랜을 변경할 수 있습니다. 버튼 클릭 시 즉시 구독 상태가 변경됩니다.'
                  : 'You can change plans without payment. Clicking a button will immediately change your subscription status.'}
              </p>
            </div>
          )}

          {/* 현재 플랜 상태 */}
          {isAuthenticated && (
            <div className="bg-terminal-dim/20 border border-terminal-border rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-terminal-text mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-status-go" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t.pricing.currentPlanStatus || (isKo ? '현재 플랜 상태' : 'Current Plan Status')}
              </h3>
              
              {/* Team 플랜일 때만 1줄 반반(2열)로 표시 */}
              {currentPlan === 'team' ? (
                <div className="grid grid-cols-2 gap-4">
                  {/* 현재 플랜 */}
                  <div className="bg-terminal-bg/50 rounded-lg p-3">
                    <p className="text-xs text-terminal-muted mb-1">
                      {t.pricing.currentPlan || (isKo ? '현재 플랜' : 'Current Plan')}
                    </p>
                    <p className="text-lg font-bold text-status-go capitalize">
                      {currentPlan}
                      {isBeta && (
                        <span className="ml-2 text-xs font-normal text-terminal-muted">
                          ({t.pricing.beta || (isKo ? '베타' : 'Beta')})
                        </span>
                      )}
                    </p>
                  </div>

                  {/* 구독 정보 (시작/다음결제 or 만료) */}
                  <div className="bg-terminal-bg/50 rounded-lg p-3">
                    <p className="text-xs text-terminal-muted mb-2">
                      {isKo ? '구독 정보' : 'Subscription'}
                    </p>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px] text-terminal-muted">
                          {isBeta 
                            ? (t.pricing.betaPeriod || (isKo ? '베타 기간' : 'Beta Period'))
                            : (t.pricing.started || (isKo ? '구독 시작일' : 'Started'))}
                        </span>
                        <span className="text-[11px] font-medium text-terminal-text text-right">
                          {isBeta ? (
                            <span className="text-status-go">
                              {t.pricing.freeAccess || (isKo ? '무료 이용 중 🎉' : 'Free Access 🎉')}
                            </span>
                          ) : subscriptionStartedAt ? (
                            formatEndDate(subscriptionStartedAt)
                          ) : (
                            <span className="text-terminal-muted">-</span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px] text-terminal-muted">
                          {isCancelling 
                            ? (t.pricing.expiresOn || (isKo ? '만료 예정일' : 'Expires On'))
                            : (t.pricing.nextBilling || (isKo ? '다음 결제일' : 'Next Billing'))}
                        </span>
                        <span className="text-[11px] font-medium text-terminal-text text-right">
                          {isCancelling && subscriptionEndsAt ? (
                            <span className="text-status-warning">
                              {formatEndDate(subscriptionEndsAt)}
                            </span>
                          ) : isBeta ? (
                            <span className="text-terminal-muted">
                              {t.pricing.afterBeta || (isKo ? '베타 종료 후 결정' : 'After Beta')}
                            </span>
                          ) : nextBillingAt ? (
                            formatEndDate(nextBillingAt)
                          ) : (
                            <span className="text-terminal-muted">-</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* 현재 플랜 */}
                  <div className="bg-terminal-bg/50 rounded-lg p-3">
                    <p className="text-xs text-terminal-muted mb-1">
                      {t.pricing.currentPlan || (isKo ? '현재 플랜' : 'Current Plan')}
                    </p>
                    <p className="text-lg font-bold text-status-go capitalize">
                      {currentPlan}
                      {isBeta && (
                        <span className="ml-2 text-xs font-normal text-terminal-muted">
                          ({t.pricing.beta || (isKo ? '베타' : 'Beta')})
                        </span>
                      )}
                    </p>
                  </div>
                  
                  {/* 구독 시작일 / 베타 상태 */}
                  <div className="bg-terminal-bg/50 rounded-lg p-3">
                    <p className="text-xs text-terminal-muted mb-1">
                      {isBeta 
                        ? (t.pricing.betaPeriod || (isKo ? '베타 기간' : 'Beta Period'))
                        : (t.pricing.started || (isKo ? '구독 시작일' : 'Started'))}
                    </p>
                    <p className="text-sm font-medium text-terminal-text">
                      {isBeta ? (
                        <span className="text-status-go">
                          {t.pricing.freeAccess || (isKo ? '무료 이용 중 🎉' : 'Free Access 🎉')}
                        </span>
                      ) : subscriptionStartedAt ? (
                        formatEndDate(subscriptionStartedAt)
                      ) : (
                        <span className="text-terminal-muted">-</span>
                      )}
                    </p>
                  </div>
                  
                  {/* 다음 결제일 / 만료 예정일 */}
                  <div className="bg-terminal-bg/50 rounded-lg p-3">
                    <p className="text-xs text-terminal-muted mb-1">
                      {(isCancelling || (isBeta && !!subscriptionEndsAt))
                        ? (t.pricing.expiresOn || (isKo ? '만료 예정일' : 'Expires On'))
                        : (t.pricing.nextBilling || (isKo ? '다음 결제일' : 'Next Billing'))}
                    </p>
                    <p className="text-sm font-medium text-terminal-text">
                      {isCancelling && subscriptionEndsAt ? (
                        <span className="text-status-warning">
                          {formatEndDate(subscriptionEndsAt)}
                        </span>
                      ) : isBeta ? (
                        subscriptionEndsAt ? (
                          <span className="text-status-warning">
                            {formatEndDate(subscriptionEndsAt)}
                          </span>
                        ) : (
                          <span className="text-terminal-muted">
                            {t.pricing.afterBeta || (isKo ? '베타 종료 후 결정' : 'After Beta')}
                          </span>
                        )
                      ) : nextBillingAt ? (
                        formatEndDate(nextBillingAt)
                      ) : (
                        <span className="text-terminal-muted">-</span>
                      )}
                    </p>
                  </div>
                </div>
              )}
              
              {/* 해지 예정 경고 */}
              {((isCancelling && subscriptionEndsAt) || (isBeta && subscriptionEndsAt)) && (
                <div className="mt-3 p-3 bg-status-warning/10 border border-status-warning/30 rounded-lg flex items-center gap-2">
                  <svg className="w-4 h-4 text-status-warning flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-xs text-status-warning">
                    {(t.pricing.subscriptionExpiresWarning || (isKo 
                      ? '구독이 {date}에 만료됩니다. 만료 후 {nextDate}부터 Free 플랜으로 전환됩니다.'
                      : 'Your subscription expires on {date}. You\'ll be switched to Free plan starting {nextDate}.'
                    ))
                      .replace('{date}', formatEndDate(subscriptionEndsAt))
                      .replace('{nextDate}', formatDateObj(addDays(subscriptionEndsAt, 1)))}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* 월간/연간 토글 */}
          <div className="relative flex items-center justify-center mb-6">
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${!isAnnual ? 'text-terminal-text' : 'text-terminal-muted'}`}>
                {isKo ? '월간' : 'Monthly'}
              </span>
              <button
                onClick={() => {
                  const newPeriod = isAnnual ? 'monthly' : 'annually';
                  analytics.billingPeriodChange(newPeriod);
                  setBillingPeriod(newPeriod);
                }}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  isAnnual ? 'bg-status-go' : 'bg-terminal-dim'
                }`}
              >
                <div
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                    isAnnual ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${isAnnual ? 'text-terminal-text' : 'text-terminal-muted'}`}>
                {isKo ? '연간' : 'Annually'}
              </span>
            </div>
            {/* -20% 배지 - 토글 오른쪽에 고정 */}
            <span className={`absolute left-[calc(50%+5.5rem)] text-xs font-semibold px-2 py-1 rounded-full ${
              isAnnual ? 'text-status-go bg-status-go/20' : 'text-terminal-muted bg-terminal-dim/50'
            }`}>
              -20%
            </span>
          </div>

          {/* 플랜 4가지 카드 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 items-stretch">
            {plans.map((plan) => (
              <PlanCard
                key={plan.name}
                {...plan}
                buttonText={getButtonText(plan.name)}
                onSelect={() => handleSelectPlan(plan.name)}
                isProcessing={isProcessing}
                isAnnual={isAnnual}
              />
            ))}
          </div>

          {/* (베타 기간 혜택) */}
          <div className="bg-status-go/10 border border-status-go/30 rounded-lg p-4 mb-6">
            <p className="text-sm text-status-go">
              <span className="font-semibold">🎉 {isKo ? '베타 기간 혜택' : 'Beta Period Benefit'}:</span>{' '}
              {isKo
                ? '현재 베타 기간 동안 모든 사용자는 Max 플랜의 기능을 무료로 이용할 수 있습니다. 정식 출시 후 유료 플랜이 활성화됩니다.'
                : 'During the beta period, all users can use Max plan features for free. Paid plans will be activated after the official launch.'}
            </p>
          </div>

          {/* 구독 해지 (로컬 테스트 모드) */}
          {APP_ENV === 'local' && isAuthenticated && currentPlan !== 'free' && currentPlan !== null && (
            <div className={`rounded-lg p-4 mb-6 ${
              isCancelling 
                ? 'bg-status-warning/10 border border-status-warning/30' 
                : 'bg-terminal-dim/20 border border-terminal-border'
            }`}>
              {isCancelling && subscriptionEndsAt ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-status-warning font-medium">
                      ⏳ {isKo ? '구독 해지 예정' : 'Subscription Cancelling'}
                    </p>
                    <p className="text-xs text-terminal-muted mt-1">
                      {isKo 
                        ? `${formatEndDate(subscriptionEndsAt)}까지 현재 플랜 사용 가능` 
                        : `Current plan available until ${formatEndDate(subscriptionEndsAt)}`}
                    </p>
                  </div>
                  <button
                    onClick={handleReactivateSubscription}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-status-go text-white text-sm font-medium rounded-lg hover:bg-status-go/90 disabled:opacity-50 disabled:cursor-wait transition-colors"
                  >
                    {isProcessing 
                      ? (isKo ? '처리 중...' : 'Processing...') 
                      : (isKo ? '구독 유지하기' : 'Keep Subscription')}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-terminal-text font-medium">
                      🧪 {isKo ? '구독 해지 테스트' : 'Cancel Subscription Test'}
                    </p>
                    <p className="text-xs text-terminal-muted mt-1">
                      {isKo 
                        ? '해지 시 이번 달 말일까지 사용 가능합니다.' 
                        : 'You can use until the end of this month after cancellation.'}
                    </p>
                  </div>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-status-danger/20 text-status-danger text-sm font-medium rounded-lg border border-status-danger/30 hover:bg-status-danger/30 disabled:opacity-50 disabled:cursor-wait transition-colors"
                  >
                    {isProcessing 
                      ? (isKo ? '처리 중...' : 'Processing...') 
                      : (isKo ? '구독 해지하기' : 'Cancel Subscription')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 상세 비교 */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-terminal-text mb-4">
              {isKo ? '상세 비교' : 'Feature Comparison'}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-terminal-border">
                    <th className="text-left py-3 px-4 text-terminal-muted font-medium">
                      {isKo ? '기능' : 'Feature'}
                    </th>
                    <th className="text-center py-3 px-4 font-medium">
                      <span className={`inline-block ${currentPlan === 'free' ? 'bg-status-go/20 text-status-go border border-status-go/50 px-3 py-1 rounded-lg' : 'text-terminal-muted'}`}>
                        Free
                      </span>
                    </th>
                    <th className="text-center py-3 px-4 font-medium">
                      <span className={`inline-block ${currentPlan === 'pro' ? 'bg-status-go/20 text-status-go border border-status-go/50 px-3 py-1 rounded-lg' : 'text-terminal-muted'}`}>
                        Pro
                      </span>
                    </th>
                    <th className="text-center py-3 px-4 font-medium">
                      <span className={`inline-block ${currentPlan === 'max' ? 'bg-status-go/20 text-status-go border border-status-go/50 px-3 py-1 rounded-lg' : 'text-terminal-muted'}`}>
                        Max
                      </span>
                    </th>
                    <th className="text-center py-3 px-4 font-medium">
                      <span className={`inline-block ${currentPlan === 'team' ? 'bg-status-go/20 text-status-go border border-status-go/50 px-3 py-1 rounded-lg' : 'text-terminal-muted'}`}>
                        Team
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-terminal-border/50">
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? '월간 평가 횟수' : 'Monthly evaluations'}</td>
                    <td className="py-3 px-4 text-center text-terminal-muted">{isKo ? '3회' : '3'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '10회' : '10'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '무제한' : 'Unlimited'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '무제한' : 'Unlimited'}</td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? '테스트케이스 분석' : 'Test case analysis'}</td>
                    <td className="py-3 px-4 text-center text-terminal-muted">{isKo ? '최대 10개' : 'Up to 10'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '최대 100개' : 'Up to 100'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '최대 300개' : 'Up to 300'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '최대 300개' : 'Up to 300'}</td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? '파일 동시 첨부' : 'Files per evaluation'}</td>
                    <td className="py-3 px-4 text-center text-terminal-muted">{isKo ? '1개' : '1'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '3개' : '3'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '10개' : '10'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '10개' : '10'}</td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? 'Planning 분석 길이' : 'Planning text length'}</td>
                    <td className="py-3 px-4 text-center text-terminal-muted">{isKo ? '3,000자' : '3,000 chars'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '15,000자' : '15,000 chars'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '30,000자' : '30,000 chars'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '50,000자' : '50,000 chars'}</td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? '평가 히스토리' : 'Evaluation history'}</td>
                    <td className="py-3 px-4 text-center text-terminal-muted">{isKo ? '3건' : '3 items'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '10건' : '10 items'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '100건' : '100 items'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '100건' : '100 items'}</td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? '상세 분석 리포트' : 'Detailed analysis report'}</td>
                    <td className="py-3 px-4 text-center text-terminal-muted">{isKo ? '기본 요약' : 'Basic summary'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '상세 분석' : 'Detailed'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '상세 + 개인 트렌드' : 'Detailed + Personal Trends'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '상세 + 팀 트렌드' : 'Detailed + Team Trends'}</td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? '팀 대시보드' : 'Team Dashboard'}</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center"><span className="text-status-go">✓</span></td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? 'PDF 리포트' : 'PDF Report'}</td>
                    <td className="py-3 px-4 text-center text-terminal-muted">{isKo ? '뷰어' : 'Viewer'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '뷰어&다운로드' : 'View & Download'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '뷰어&다운로드' : 'View & Download'}</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '뷰어&다운로드' : 'View & Download'}</td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? 'PDF 워터마크 제거' : 'No PDF watermark'}</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center"><span className="text-status-go">✓</span></td>
                    <td className="py-3 px-4 text-center"><span className="text-status-go">✓</span></td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? 'API 요청' : 'API requests'}</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '200회/일' : '200/day'}</td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? 'API 키' : 'API keys'}</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '5개' : '5'}</td>
                  </tr>
                  <tr className="hover:bg-terminal-dim/10">
                    <td className="py-3 px-4 text-terminal-text">{isKo ? '팀 멤버' : 'Team members'}</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-dim">—</td>
                    <td className="py-3 px-4 text-center text-terminal-text">{isKo ? '최대 5명' : 'Up to 5'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 자주 묻는 질문 */}
          <div className="border-t border-terminal-border pt-6">
            <h3 className="text-lg font-semibold text-terminal-text mb-4">
              {isKo ? '자주 묻는 질문' : 'FAQ'}
            </h3>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-terminal-dim/10 rounded-lg p-4">
                  <p className="text-sm font-medium text-terminal-text mb-1">{faq.q}</p>
                  <p className="text-sm text-terminal-muted">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 문의 */}
          <div className="mt-6 text-center">
            <p className="text-sm text-terminal-muted">
              {isKo ? '문의사항이 있으신가요?' : 'Have questions?'}
              <br />
              <a href="mailto:support@releasegate.app" className="text-status-go hover:underline">
                support@releasegate.app
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* 새로운 인증 모달 */}
    {showAuthModal && (
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCloseAuthModal();
          }
        }}
      >
        <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-terminal-border bg-terminal-dim/20">
            <h3 className="text-base font-semibold text-terminal-text">{t.auth.quickLoginTitle}</h3>
            <p className="text-xs text-terminal-muted mt-1">{t.auth.quickLoginDesc}</p>
          </div>

          <div className="p-6 space-y-3">
            {/* 이메일 로그인 버튼 */}
            <button
              onClick={() => {
                setShowAuthModal(false);
                analytics.loginClick();
                setShowEmailAuthModal(true);
              }}
              disabled={isProcessing}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-terminal-border rounded-lg text-sm text-terminal-text hover:bg-terminal-dim/30 hover:border-status-go/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="text-base leading-none">✉️</span>
              <span>{t.auth.loginWithEmail}</span>
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-terminal-border"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-terminal-bg text-terminal-muted">{t.auth.or}</span>
              </div>
            </div>

            {/* GitHub 로그인 버튼 */}
            <button
              onClick={() => {
                setShowAuthModal(false);
                analytics.loginClick();
                startGithubLogin();
              }}
              disabled={isProcessing}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-terminal-border rounded-lg text-sm text-terminal-text hover:bg-terminal-dim/30 hover:border-status-go/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="text-base leading-none">🐙</span>
              <span>{t.auth.loginWithGithub}</span>
            </button>
          </div>

          <div className="px-6 py-4 border-t border-terminal-border bg-terminal-dim/10">
            <button
              onClick={handleCloseAuthModal}
              disabled={isProcessing}
              className="w-full px-4 py-2 text-xs text-terminal-muted hover:text-terminal-text transition-colors"
            >
              {t.terms.cancel}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* 이메일 인증 모달 */}
    <EmailAuthModal
      isOpen={showEmailAuthModal}
      onClose={() => setShowEmailAuthModal(false)}
      initialMode="login"
    />
    </>
  );
}

